import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw, SkipForward, Play } from 'lucide-react';
import syncService from '../services/syncService';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import './ErrorAnalysisPage.css';

const SEVERITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low', info: 'Info' };

export default function ErrorAnalysisPage() {
    const { syncId, taskId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [analysis, setAnalysis] = useState(null);    // { analysedCount, model, groups } from the backend
    const [busyGroup, setBusyGroup] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);
    const [toast, setToast] = useState(null);
    const [progressNote, setProgressNote] = useState('');

    // Poll cadence and ceiling. The job reads up to 3000 records then calls the LLM; 2s is responsive
    // without hammering, and the ceiling stops a wedged job polling forever in a background tab.
    const POLL_MS = 2000;
    const POLL_TIMEOUT_MS = 5 * 60 * 1000;
    const pollRef = useRef({ cancelled: false });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setProgressNote('Starting analysis…');
        // Supersede any in-flight poll: a re-analyse must not leave the previous job's poll running,
        // or a late response from the old job could overwrite the new one's result.
        pollRef.current.cancelled = true;
        const me = { cancelled: false };
        pollRef.current = me;
        try {
            // POST starts a job and returns 202 immediately — the work exceeds API Gateway's 29s limit,
            // so it runs async server-side and we poll for the outcome.
            const started = await syncService.startErrorAnalysis(syncId, taskId);
            const jobId = started?.jobId;
            if (!jobId) throw new Error('No job id returned from the analysis request');

            const deadline = Date.now() + POLL_TIMEOUT_MS;
            for (;;) {
                if (me.cancelled) return;                       // superseded or unmounted
                await new Promise(r => setTimeout(r, POLL_MS));
                if (me.cancelled) return;
                if (Date.now() > deadline) throw new Error('Analysis timed out — the job is still running server-side; try Re-analyse in a moment');

                const st = await syncService.getErrorAnalysisStatus(syncId, taskId, jobId);
                if (me.cancelled) return;

                if (st?.status === 'done') {
                    const res = st.result || {};
                    setAnalysis({
                        total: res.analysedCount ?? st.recordCount ?? 0,
                        model: res.model || null,
                        groups: Array.isArray(res.groups) ? res.groups : [],
                    });
                    return;
                }
                if (st?.status === 'error') throw new Error(st.error || 'Error analysis failed');
                setProgressNote(st?.status === 'running' ? 'Analysing error records…' : 'Queued…');
            }
        } catch (e) {
            if (!me.cancelled) setError(e.response?.data?.message || e.message || 'Failed to analyse errors');
        } finally {
            if (!me.cancelled) setLoading(false);
        }
    }, [syncId, taskId]);

    useEffect(() => {
        load();
        // Stop polling when the page unmounts, so navigating away does not leave a timer running.
        return () => { pollRef.current.cancelled = true; };
    }, [load]);

    const showToast = (msg, kind = 'success') => { setToast({ msg, kind }); setTimeout(() => setToast(null), 4000); };

    // Bulk-act on a whole group. reprocess auto-requests any missing profiles named in the records.
    const runGroupAction = async (group, action) => {
        const recordIds = group.recordIds;
        if (!recordIds.length) return;
        setBusyGroup(group.id);
        try {
            // The backend reprocess now auto-parses each record's "Missing linked Profile <id>" comment
            // and requests those profiles first, so no explicit profile list is needed here.
            const res = await syncService.bulkActionRecords(syncId, taskId, action, recordIds);
            const n = res?.updated ?? recordIds.length;
            showToast(`${action === 'skip' ? 'Skipped' : 'Reprocessed'} ${n} record(s)${res?.profilesRequested ? `, requested ${res.profilesRequested} profile(s)` : ''}.`);
            await load();
        } catch (e) {
            showToast(e.response?.data?.message || 'Action failed', 'error');
        } finally {
            setBusyGroup(null);
        }
    };

    const confirmGroupAction = (group, action) => {
        setConfirmModal({
            title: action === 'skip' ? 'Skip these records' : 'Reprocess these records',
            message:
                `${action === 'skip' ? 'Skip' : 'Reprocess'} all ${group.count} record(s) in "${group.title}"?` +
                (action === 'reprocess' && group.id === 'missing-profile'
                    ? ' Missing linked profiles will be requested from the source first.'
                    : ''),
            confirmLabel: action === 'skip' ? 'Skip' : 'Reprocess',
            danger: action === 'skip',
            onConfirm: async () => { await runGroupAction(group, action); },
        });
    };

    const parentUrl = `/sync/${syncId}/task/${taskId}/configure?from=tasks`;

    return (
        <div className="acp-page ea-page">
            <button className="ea-back-btn" onClick={() => navigate(parentUrl)}>
                <ArrowLeft size={16} /> Back to Task
            </button>

            <div className="acp-card">
                <div className="acp-card-header ea-header">
                    <div>
                        <h1 className="acp-title">Error Analysis</h1>
                        <p className="acp-subtitle">
                            Error, pending &amp; skipped records grouped by the action they need
                            {analysis?.model ? ` · analysed by ${analysis.model}` : ''}.
                        </p>
                    </div>
                    <button className="ea-refresh-btn" onClick={load} disabled={loading}>
                        <RefreshCw size={13} /> {loading ? 'Analysing…' : 'Re-analyse'}
                    </button>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    {error && <div className="acp-error-banner"><AlertCircle className="icon-sm" /> {error}</div>}

                    {loading ? (
                        <div className="ea-loading"><Spinner size="md" /><p>{progressNote || 'Analysing error records…'}</p></div>
                    ) : !analysis || analysis.total === 0 ? (
                        <div className="ea-empty">
                            <AlertCircle className="ea-empty-icon" />
                            <p>No error, pending or skipped records to analyse.</p>
                        </div>
                    ) : (
                        <>
                            <p className="ea-summary-line">
                                Analysed <strong>{analysis.total}</strong> record(s) into{' '}
                                <strong>{analysis.groups.length}</strong> action group(s).
                            </p>

                            <div className="ea-groups">
                                {analysis.groups.map((g) => (
                                    <div key={g.id} className={`ea-group ea-sev-${g.severity}`}>
                                        <div className="ea-group-head">
                                            <span className={`ea-sev-chip ea-sev-chip-${g.severity}`}>{SEVERITY_LABEL[g.severity]}</span>
                                            <span className="ea-group-title">{g.title}</span>
                                            <span className="ea-group-count">{g.count.toLocaleString()}</span>
                                        </div>
                                        <p className="ea-group-rec">{g.recommendation}</p>

                                        {g.sourceIds.length > 0 && (
                                            <div className="ea-ids">
                                                <span className="ea-ids-label">Source IDs:</span>{' '}
                                                {g.sourceIds.slice(0, 20).join(', ')}
                                                {g.sourceIds.length > 20 && <span className="ea-ids-more"> +{g.sourceIds.length - 20} more</span>}
                                            </div>
                                        )}

                                        {(g.action === 'reprocess' || g.action === 'skip') && g.recordIds.length > 0 && (
                                            <div className="ea-group-actions">
                                                {g.action === 'reprocess' && (
                                                    <button
                                                        className="ea-action-btn ea-action-reprocess"
                                                        onClick={() => confirmGroupAction(g, 'reprocess')}
                                                        disabled={busyGroup === g.id}
                                                    >
                                                        {busyGroup === g.id
                                                            ? <><Spinner size="sm" className="icon-sm" /> Working…</>
                                                            : <><Play size={13} /> Reprocess {g.count}</>}
                                                    </button>
                                                )}
                                                <button
                                                    className="ea-action-btn ea-action-skip"
                                                    onClick={() => confirmGroupAction(g, 'skip')}
                                                    disabled={busyGroup === g.id}
                                                >
                                                    <SkipForward size={13} /> Skip {g.count}
                                                </button>
                                            </div>
                                        )}
                                        {g.action === 'review' && <div className="ea-action-note">Needs a decision or a fix elsewhere — no bulk action.</div>}
                                        {g.action === 'none' && <div className="ea-action-note">Informational — no action needed.</div>}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {toast && <div className={`ea-toast ea-toast-${toast.kind}`}>{toast.msg}</div>}

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger={confirmModal.danger}
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
}
